import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { Braces, Check, FileCode, FileText, Loader2, X } from "lucide-react";
import glyph from "@/assets/intelligenic-glyph.svg";
import { useIngestionBatch, type FileIngestState, type IngestStage } from "./useIngestionBatch";

/** Types the extract_text pipeline can handle. */
export const ACCEPTED_FILE_TYPES = ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.html,.json,.md";

export interface IngestionCardProps {
    initialFiles?: File[];
    /** Current product/project id (KAN-1584 follow-up), passed straight
     *  through to useIngestionBatch/useFileUpload so uploads auto-scope to
     *  the current product. Omit when there's no current product. */
    projectId?: number;
    /** Show the 1A drop-zone/picker while the batch is empty (tool-card mode). */
    allowPicker?: boolean;
    filenameHint?: string;
    onFileIndexed?: (r: { fileId: number; filename: string }) => void;
    onSettled?: (summary: {
        indexed: { fileId: number; filename: string }[];
        failed: { filename: string; error: string }[];
    }) => void;
    onBusyChange?: (busy: boolean) => void;
    /** Fired at most once if the chat session is cleared/switched mid-batch,
     *  unmounting this card while a file is still uploading/queued. Passed
     *  straight through to useIngestionBatch — see its doc comment. */
    onAborted?: (summary: {
        indexed: { fileId: number; filename: string }[];
        incomplete: string[];
    }) => void;
}

// Design tokens — docs/design/ingestion-card-1a/README.md ("Design Tokens").
const T = {
    primary: "#94b2ff",
    primarySoft: "rgba(148,178,255,0.07)",
    blue200: "#f7f9ff",
    success: "#059669",
    error: "#dc2626",
    warning: "#d97706",
    slate100: "#f1f5f9",
    slate200: "#e2e8f0",
    fg1: "#111827",
    fg2: "#374151",
    fg3: "#4b5563",
    fg4: "#6b7280",
    bgPaper: "#ffffff",
    bgPanel: "#f8fafc",
    border: "rgba(0,0,0,0.07)"
};
const MONO = "'JetBrains Mono', monospace";

// The five pipeline stages shown in the header/mini-stepper (README "Pipeline Stages").
// "queued" and "error" are separate row states, not pipeline steps.
const STAGES: { key: IngestStage; label: string }[] = [
    { key: "uploading", label: "Uploading" },
    { key: "parsing", label: "Parsing" },
    { key: "chunking", label: "Chunking" },
    { key: "embedding", label: "Embedding" },
    { key: "indexed", label: "Indexed" }
];

type RowKind = "queued" | "active" | "indexed" | "failed";

function rowKind(f: FileIngestState): RowKind {
    if (f.stage === "queued") return "queued";
    if (f.stage === "indexed") return "indexed";
    if (f.stage === "error") return "failed";
    return "active";
}

/** `2.4 MB` (one decimal) for MB-scale files, whole `NN KB` below that. */
function formatBytes(bytes: number): string {
    const mb = bytes / (1024 * 1024);
    if (mb >= 1) return `${mb.toFixed(1)} MB`;
    return `${Math.round(bytes / 1024)} KB`;
}

function metadataFor(f: FileIngestState): string {
    const size = formatBytes(f.sizeBytes);
    if (f.stage === "queued") return `${size} · waiting`;
    if (f.stage === "error") return f.errorReason ?? "Upload failed";
    const parts = [size];
    if (f.chunks != null) parts.push(`${f.chunks} chunks`);
    if (f.vectors != null) parts.push(`${f.vectors} vectors`);
    return parts.join(" · ");
}

// Extensions rendered with the `.json`-specific icon vs. the general code-file icon
// (README "Assets": Braces/FileCode for code files, FileText for everything else).
const CODE_EXTENSIONS = new Set([
    ".js", ".jsx", ".ts", ".tsx", ".py", ".java", ".cs", ".cpp", ".c", ".h", ".go", ".rb", ".rs", ".php", ".sh",
    ".sql", ".html", ".css", ".scss", ".yml", ".yaml", ".xml"
]);

/** Picks the row icon by file extension (case-insensitive); files without one get FileText. */
function iconForFilename(name: string): typeof FileText {
    const dot = name.lastIndexOf(".");
    const ext = dot >= 0 ? name.slice(dot).toLowerCase() : "";
    if (ext === ".json") return Braces;
    if (CODE_EXTENSIONS.has(ext)) return FileCode;
    return FileText;
}

function headerTitle(status: string, total: number, indexedCount: number, failedCount: number): string {
    if (status === "complete") return `${total} files indexed`;
    if (status === "partial") return `${indexedCount} of ${total} indexed · ${failedCount} failed`;
    return total === 1 ? "Vectorizing 1 file" : `Vectorizing ${total} files`;
}

/** Wraps a lucide icon so it can carry a one-shot/looping CSS animation via sx. */
function Spin({ size, color }: { size: number; color: string }) {
    return (
        <Box
            component="span"
            sx={{
                display: "inline-flex",
                animation: "cn-ingest-spin 1s linear infinite",
                "@keyframes cn-ingest-spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } }
            }}
        >
            <Loader2 size={size} color={color} />
        </Box>
    );
}

function MiniStepper({ stage, progress }: { stage: IngestStage; progress: number }) {
    const currentIndex = STAGES.findIndex(s => s.key === stage);
    return (
        <Box sx={{ display: "flex", alignItems: "center", mt: "7px" }}>
            {STAGES.map((s, i) => {
                const state = i < currentIndex ? "done" : i === currentIndex ? "active" : "upcoming";
                const color = state === "done" ? T.success : state === "active" ? T.primary : T.fg4;
                return (
                    <Box key={s.key} sx={{ display: "flex", alignItems: "center", flex: i < STAGES.length - 1 ? 1 : "0 0 auto" }}>
                        <Typography
                            sx={{ fontSize: "10px", fontWeight: state === "active" ? 700 : 600, color, whiteSpace: "nowrap" }}
                        >
                            {s.label}
                        </Typography>
                        {i < STAGES.length - 1 && (
                            <Box
                                sx={{
                                    flex: 1,
                                    height: "3px",
                                    borderRadius: "999px",
                                    mx: "6px",
                                    background:
                                        state === "done"
                                            ? T.success
                                            : state === "active"
                                              ? `linear-gradient(90deg, ${T.primary} ${progress}%, ${T.slate200} ${progress}%)`
                                              : T.slate200,
                                    transition: "background 200ms ease-out"
                                }}
                            />
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}

function StatusLabel({ f, kind }: { f: FileIngestState; kind: RowKind }) {
    if (kind === "queued") {
        return (
            <Typography sx={{ fontSize: "11px", fontWeight: 500, color: T.fg4, whiteSpace: "nowrap" }}>Queued</Typography>
        );
    }
    if (kind === "indexed") {
        return (
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <Check size={13} color={T.success} />
                <Typography sx={{ fontSize: "11px", fontWeight: 600, color: T.success, whiteSpace: "nowrap" }}>
                    Indexed
                </Typography>
            </Box>
        );
    }
    if (kind === "failed") {
        return (
            <Typography sx={{ fontSize: "11px", fontWeight: 600, color: T.error, whiteSpace: "nowrap" }}>Failed</Typography>
        );
    }
    // active: gerund label, "· NN%" only for the measurable upload/embed stages.
    const stageDef = STAGES.find(s => s.key === f.stage);
    const suffix = f.stage === "uploading" || f.stage === "embedding" ? ` · ${f.progress}%` : "";
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <Spin size={12} color={T.primary} />
            <Typography sx={{ fontSize: "11px", fontWeight: 600, color: T.primary, whiteSpace: "nowrap" }}>
                {stageDef?.label ?? ""}
                {suffix}
            </Typography>
        </Box>
    );
}

// Per-state row icon color (README "File row" states: Active/Queued/Complete/Failed).
const ROW_ICON_COLOR: Record<RowKind, string> = {
    active: T.primary,
    queued: T.fg3,
    indexed: T.fg2,
    failed: T.error
};

function FileRow({
    f,
    onRemove,
    onRetry
}: {
    f: FileIngestState;
    onRemove: (id: string) => void;
    onRetry: (id: string) => void;
}) {
    const kind = rowKind(f);
    const isActive = kind === "active";
    const RowIcon = iconForFilename(f.name);
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
                padding: "6px 8px",
                borderRadius: isActive ? "10px" : 0,
                backgroundColor: isActive ? T.primarySoft : "transparent",
                opacity: kind === "queued" ? 0.72 : 1,
                transition: "background-color 150ms ease-out"
            }}
        >
            <Box
                sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: isActive ? T.bgPaper : T.slate100,
                    border: `1px solid ${isActive ? T.blue200 : T.border}`
                }}
            >
                <RowIcon size={16} color={ROW_ICON_COLOR[kind]} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                    <Typography
                        sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: T.fg1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            minWidth: 0
                        }}
                    >
                        {f.name}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
                        <StatusLabel f={f} kind={kind} />
                        {kind === "queued" && (
                            <Box
                                component="button"
                                type="button"
                                aria-label={`Remove ${f.name}`}
                                onClick={() => onRemove(f.id)}
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    border: "none",
                                    background: "none",
                                    color: T.fg4,
                                    cursor: "pointer",
                                    padding: "2px",
                                    "&:focus-visible": { outline: `2px solid ${T.primary}`, outlineOffset: "1px" }
                                }}
                            >
                                <X size={13} />
                            </Box>
                        )}
                        {kind === "failed" && (
                            <Box
                                component="button"
                                type="button"
                                aria-label={`Retry ${f.name}`}
                                onClick={() => onRetry(f.id)}
                                sx={{
                                    border: "none",
                                    background: "none",
                                    color: T.primary,
                                    fontSize: "11px",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    padding: 0,
                                    "&:focus-visible": { outline: `2px solid ${T.primary}`, outlineOffset: "1px" }
                                }}
                            >
                                Retry
                            </Box>
                        )}
                    </Box>
                </Box>
                <Typography sx={{ fontFamily: MONO, fontSize: "11px", color: T.fg4 }}>{metadataFor(f)}</Typography>
                {isActive && <MiniStepper stage={f.stage} progress={f.progress} />}
            </Box>
        </Box>
    );
}

function Picker({ filenameHint, onFiles }: { filenameHint?: string; onFiles: (files: File[]) => void }) {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [dragOver, setDragOver] = useState(false);
    return (
        <Box
            onDragOver={e => {
                e.preventDefault();
                setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => {
                e.preventDefault();
                setDragOver(false);
                const dropped = e.dataTransfer.files;
                if (dropped && dropped.length) onFiles(Array.from(dropped));
            }}
            onClick={() => inputRef.current?.click()}
            sx={{
                border: `1px dashed ${dragOver ? T.primary : T.slate200}`,
                borderRadius: "12px",
                padding: "20px",
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: dragOver ? T.primarySoft : T.bgPanel,
                transition: "background-color 150ms ease-out, border-color 150ms ease-out"
            }}
        >
            <input
                ref={inputRef}
                type="file"
                multiple
                accept={ACCEPTED_FILE_TYPES}
                data-testid="cn-ingest-file-input"
                style={{ display: "none" }}
                onChange={e => {
                    const chosen = e.target.files;
                    if (chosen && chosen.length) onFiles(Array.from(chosen));
                    e.target.value = "";
                }}
            />
            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: T.fg1 }}>
                Drop files here or click to choose
            </Typography>
            {filenameHint && (
                <Typography sx={{ fontSize: "12px", color: T.fg4, mt: "4px" }}>{filenameHint}</Typography>
            )}
        </Box>
    );
}

export default function IngestionCard({
    initialFiles,
    projectId,
    allowPicker,
    filenameHint,
    onFileIndexed,
    onSettled,
    onBusyChange,
    onAborted
}: IngestionCardProps): React.ReactElement {
    const { files, status, batchProgress, indexedCount, failedCount, addFiles, removeFile, retryFile } =
        useIngestionBatch({ initialFiles, projectId, onFileIndexed, onSettled, onAborted });

    useEffect(() => {
        onBusyChange?.(status === "in_progress");
    }, [status, onBusyChange]);

    const [expanded, setExpanded] = useState(false);
    const prevStatusRef = useRef(status);
    useEffect(() => {
        if ((status === "complete" || status === "partial") && prevStatusRef.current !== status) {
            setExpanded(false);
        }
        prevStatusRef.current = status;
    }, [status]);

    if (files.length === 0) {
        return allowPicker ? <Picker filenameHint={filenameHint} onFiles={addFiles} /> : <></>;
    }

    const settled = status === "complete" || status === "partial";
    const indexedRows = files.filter(f => f.stage === "indexed");
    const otherRows = files.filter(f => f.stage !== "indexed");
    const totalChunks = indexedRows.reduce((sum, f) => sum + (f.chunks ?? 0), 0);

    return (
        <Box
            aria-live="polite"
            sx={{
                width: "100%",
                backgroundColor: T.bgPaper,
                border: `1px solid ${T.border}`,
                borderRadius: "16px",
                boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                overflow: "hidden",
                animation: "cn-ingest-enter 220ms ease-out",
                "@keyframes cn-ingest-enter": {
                    from: { opacity: 0, transform: "translateY(4px)" },
                    to: { opacity: 1, transform: "translateY(0)" }
                }
            }}
        >
            <Box sx={{ padding: "15px 17px 13px", borderBottom: `1px solid ${T.border}` }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "12px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "9px" }}>
                        {status === "in_progress" ? <Spin size={16} color={T.primary} /> : <Check size={16} color={T.success} />}
                        <Typography
                            sx={{ fontSize: "13.5px", fontWeight: 600, color: status === "partial" ? T.warning : T.fg1 }}
                        >
                            {headerTitle(status, files.length, indexedCount, failedCount)}
                        </Typography>
                    </Box>
                    {status === "in_progress" && (
                        <Typography sx={{ fontFamily: MONO, fontSize: "12px", fontWeight: 600, color: T.primary }}>
                            {batchProgress}%
                        </Typography>
                    )}
                </Box>
                {status === "in_progress" && (
                    <Box
                        role="progressbar"
                        aria-valuenow={batchProgress}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        sx={{ height: "6px", borderRadius: "999px", backgroundColor: T.slate200, overflow: "hidden" }}
                    >
                        <Box
                            sx={{
                                height: "100%",
                                width: `${batchProgress}%`,
                                backgroundColor: T.primary,
                                borderRadius: "999px",
                                transition: "width 200ms ease-out"
                            }}
                        />
                    </Box>
                )}
            </Box>

            <Box
                sx={{
                    padding: "8px 9px 10px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "2px",
                    borderBottom: `1px solid ${T.border}`
                }}
            >
                {settled ? (
                    <>
                        {expanded ? (
                            indexedRows.map(f => <FileRow key={f.id} f={f} onRemove={removeFile} onRetry={retryFile} />)
                        ) : indexedRows.length > 0 ? (
                            <Box
                                component="button"
                                type="button"
                                onClick={() => setExpanded(true)}
                                sx={{
                                    fontFamily: MONO,
                                    fontSize: "11px",
                                    color: T.fg4,
                                    textAlign: "left",
                                    border: "none",
                                    background: "none",
                                    cursor: "pointer",
                                    padding: "4px 0"
                                }}
                            >
                                {`${indexedRows.length} ${indexedRows.length === 1 ? "file" : "files"} · ${totalChunks} chunks indexed`}
                            </Box>
                        ) : null}
                        {otherRows.map(f => <FileRow key={f.id} f={f} onRemove={removeFile} onRetry={retryFile} />)}
                    </>
                ) : (
                    files.map(f => <FileRow key={f.id} f={f} onRemove={removeFile} onRetry={retryFile} />)
                )}
            </Box>

            <Box sx={{ padding: "11px 17px", backgroundColor: T.bgPanel, display: "flex", alignItems: "center", gap: "8px" }}>
                <img src={glyph} width={14} height={14} alt="" />
                <Typography sx={{ fontSize: "11.5px", color: T.fg3 }}>
                    {status === "complete" ? (
                        "Woven into the Context Mesh."
                    ) : status === "partial" ? (
                        `Woven into the Context Mesh — ${failedCount} ${failedCount === 1 ? "file" : "files"} not included.`
                    ) : (
                        <>
                            Adding to the <strong style={{ color: T.fg2 }}>Context Mesh</strong> — available to the engine as it
                            works.
                        </>
                    )}
                </Typography>
            </Box>
        </Box>
    );
}
